import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import * as S from './style'
import filterIcon from '../../../assets/stock/TableMovementHeader/filter.svg'
import { OptionsButton, SubHeaderContainer, LeftSide, RightSide, ButtonCreate } from '../../../components/styles/subheader-style'
import { Option, OptionsList } from '../Pipelines/PipelineOptionsDropdown/style'
import { Plus, Tag, Ellipsis } from 'lucide-react'
import { RefreshIcon, RefreshIconContainer } from '../../../components/styles/subheader-style'
import refresh_act from '../../../assets/refresh-active.svg'
import ViewOrEditThemes from '../../../components/Collections/Modals/ViewOrEditThemes'

export interface CollectionsOutletType {
  refresh: number
  search: string
  Styled: typeof S
  isLoading?: boolean
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
}

const Collections: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [search, setSearch] = useState<string>('')
  const [refresh, setRefresh] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [showOptions, setShowOptions] = useState<boolean>(false)
  const optionsRef = useRef<HTMLUListElement | null>(null)
  const optionsButtonRef = useRef<HTMLDivElement | null>(null)
  const [optionsPosition, setOptionsPosition] = useState<{ top: number; right: number } | null>(null)
  const [collections, setCollections] = useState<Array<{ id: number; name: string }>>([])
  const [collectionThemes, setCollectionThemes] = useState<Array<{ id: number; name: string; color?: string }>>([])
  const [selectedCollectionForThemes, setSelectedCollectionForThemes] = useState<number | null>(null)
  const [showThemeModal, setShowThemeModal] = useState<boolean>(false)

  const handleNewCollection = () => {
    window.location.href = '/panel/collection/register'
  }

  const handleViewThemes = () => {
    if (collections.length === 0) {
      setShowOptions(false)
      return
    }

    if (!selectedCollectionForThemes) {
      setSelectedCollectionForThemes(collections[0].id)
    }

    setShowThemeModal(true)
    setShowOptions(false)
  }

  const toggleOptions = () => {
    setShowOptions((previous) => {
      if (previous) {
        return false
      }

      if (optionsButtonRef.current) {
        const buttonRect = optionsButtonRef.current.getBoundingClientRect()
        const top = buttonRect.bottom + 8
        const right = Math.max(16, window.innerWidth - buttonRect.right)
        setOptionsPosition({ top, right })
      }

      return true
    })
  }

  const outletContext: CollectionsOutletType = {
    refresh,
    search,
    Styled: S,
    isLoading,
    setIsLoading,
  }


  const isRegisterOrEdit =
    location.pathname.includes('/register') ||
    location.pathname.includes('/edit')

  const isInsideProductDevelopment = location.pathname.startsWith('/panel/collection')

  useEffect(() => {
    const handleProductDevelopmentRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'collection') {
        setRefresh((previous) => previous + 1)
      }
    }

    const handleProductDevelopmentCreate = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'collection') {
        navigate('/panel/collection/register')
      }
    }

    window.addEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
    window.addEventListener('product-development-create', handleProductDevelopmentCreate as EventListener)

    return () => {
      window.removeEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
      window.removeEventListener('product-development-create', handleProductDevelopmentCreate as EventListener)
    }
  }, [navigate])

  useEffect(() => {
    const handleClickOutsideOptions = (event: MouseEvent) => {
      const target = event.target as Node
      if (optionsRef.current && optionsRef.current.contains(target)) {
        return
      }
      if (optionsButtonRef.current && optionsButtonRef.current.contains(target)) {
        return
      }
      if (showOptions) {
        setShowOptions(false)
      }
    }

    const handleLayoutChange = () => {
      if (showOptions) {
        setShowOptions(false)
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutsideOptions)
      window.addEventListener('scroll', handleLayoutChange, true)
      window.addEventListener('resize', handleLayoutChange)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideOptions)
      window.removeEventListener('scroll', handleLayoutChange, true)
      window.removeEventListener('resize', handleLayoutChange)
    }
  }, [showOptions])

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/collections?limit=100`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        const data = await response.json()
        const list = Array.isArray(data) ? data : data.content || []
        setCollections(list.map((c: any) => ({ id: c.id, name: c.name || `Coleção ${c.id}` })))
        if (list.length > 0 && selectedCollectionForThemes === null) {
          setSelectedCollectionForThemes(list[0].id)
        }
      } catch (err) {
        console.error('Falha ao carregar coleções para temas:', err)
      }
    }

    loadCollections()
  }, [selectedCollectionForThemes])

  if (isRegisterOrEdit) {
    return <Outlet context={outletContext} />
  }

  return (
    <>
      {!isInsideProductDevelopment && (
        <SubHeaderContainer>
          <LeftSide>
            <S.Title>Coleções</S.Title>
          </LeftSide>
          <RightSide style={{ position: 'relative', alignItems: 'center' }}>
            <RefreshIconContainer onClick={() => setRefresh(prev => prev + 1)} title="Atualizar">
              <RefreshIcon src={refresh_act} alt="Atualizar" $spinning={isLoading} />
            </RefreshIconContainer>
            <OptionsButton ref={optionsButtonRef} onClick={toggleOptions} style={{ position: 'relative' }}>
              <Ellipsis size={18} />
            </OptionsButton>
            {showOptions && optionsPosition && ReactDOM.createPortal(
              <OptionsList ref={optionsRef} style={{ top: `${optionsPosition.top}px`, right: `${optionsPosition.right}px`, left: 'auto', position: 'fixed', zIndex: 2147483647, minWidth: 'unset', width: 'max-content' }}>
                <Option onClick={() => { handleViewThemes(); setShowOptions(false) }}>
                  <Tag size={18} /> Temas
                </Option>
              </OptionsList>,
              document.body
            )}
            <ButtonCreate onClick={handleNewCollection}>
              <Plus size={18} />
              Nova coleção
            </ButtonCreate>
          </RightSide>
        </SubHeaderContainer>
      )}

      <S.Content>
        <Outlet context={outletContext} />
      </S.Content>

      {showThemeModal && selectedCollectionForThemes && (
        <ViewOrEditThemes
          collectionId={selectedCollectionForThemes}
          closeModal={() => setShowThemeModal(false)}
          onSave={(updatedThemes) => {
            setCollectionThemes(updatedThemes)
            setShowThemeModal(false)
          }}
        />
      )}
    </>
  )
}

export default Collections
